import type {
  CurrentUser,
  DiscoverQuery,
  Friend,
  FriendRequest,
  Page,
  PublicUser,
  UpdateProfileInput,
  UserWithRelation,
} from '@talkvibe/shared';
import { api } from './api.ts';

export const userService = {
  profile: (username: string): Promise<UserWithRelation> =>
    api.get<UserWithRelation>(`/api/users/${encodeURIComponent(username)}`),

  updateProfile: (input: UpdateProfileInput): Promise<CurrentUser> =>
    api.patch<CurrentUser>('/api/me/profile', input),

  discover: (query: DiscoverQuery): Promise<Page<UserWithRelation>> =>
    api.get<Page<UserWithRelation>>('/api/discover', {
      query: {
        country: query.country,
        language: query.language,
        interest: query.interest,
        username: query.username,
        cursor: query.cursor,
        limit: query.limit,
      },
    }),

  /** Uploads through the Worker, which validates and stores it in R2. */
  uploadImage: (kind: 'avatar' | 'cover', file: File): Promise<CurrentUser> => {
    const form = new FormData();
    form.append('file', file);
    return api.post<CurrentUser>(`/api/me/${kind}`, form);
  },

  connect: (userId: string): Promise<{ result: 'request_sent' | 'friends' }> =>
    api.post<{ result: 'request_sent' | 'friends' }>(`/api/connect/${encodeURIComponent(userId)}`),

  friends: (cursor?: string): Promise<Page<Friend>> =>
    api.get<Page<Friend>>('/api/friends', { query: { cursor } }),

  friendRequests: (direction: 'incoming' | 'outgoing' = 'incoming', cursor?: string): Promise<Page<FriendRequest>> =>
    api.get<Page<FriendRequest>>('/api/friend-requests', { query: { direction, cursor } }),

  acceptRequest: (id: string): Promise<void> =>
    api.post(`/api/friend-requests/${encodeURIComponent(id)}/accept`).then(() => undefined),

  rejectRequest: (id: string): Promise<void> =>
    api.post(`/api/friend-requests/${encodeURIComponent(id)}/reject`).then(() => undefined),

  cancelRequest: (id: string): Promise<void> =>
    api.post(`/api/friend-requests/${encodeURIComponent(id)}/cancel`).then(() => undefined),

  /* --- Following: asymmetric, no approval, separate from friendship --- */

  follow: (userId: string): Promise<void> =>
    api.post(`/api/users/${encodeURIComponent(userId)}/follow`).then(() => undefined),

  unfollow: (userId: string): Promise<void> =>
    api.delete(`/api/users/${encodeURIComponent(userId)}/follow`).then(() => undefined),

  /**
   * Somebody else's friends.
   *
   * `total` beside the page, because the grid on a profile shows a count next to
   * six faces and that count is the whole friendship total, not the page size.
   * It is also the real total: people the viewer may not see are filtered out of
   * `items` and still counted here.
   */
  friendsOf: (
    userId: string,
    cursor?: string,
  ): Promise<Page<PublicUser> & { total: number }> =>
    api.get<Page<PublicUser> & { total: number }>(
      `/api/users/${encodeURIComponent(userId)}/friends`,
      { query: { cursor } },
    ),

  followers: (userId: string, cursor?: string): Promise<Page<PublicUser>> =>
    api.get<Page<PublicUser>>(`/api/users/${encodeURIComponent(userId)}/followers`, {
      query: { cursor },
    }),

  following: (userId: string, cursor?: string): Promise<Page<PublicUser>> =>
    api.get<Page<PublicUser>>(`/api/users/${encodeURIComponent(userId)}/following`, {
      query: { cursor },
    }),

  /* --- Blocking --- */

  /** People this account has blocked. Nobody else can read this list. */
  blocked: (cursor?: string): Promise<Page<PublicUser>> =>
    api.get<Page<PublicUser>>('/api/blocked', { query: { cursor } }),

  block: (userId: string): Promise<void> =>
    api.post(`/api/users/${encodeURIComponent(userId)}/block`).then(() => undefined),

  unblock: (userId: string): Promise<void> =>
    api.delete(`/api/users/${encodeURIComponent(userId)}/block`).then(() => undefined),

  unfriend: (userId: string): Promise<void> =>
    api.delete(`/api/friends/${encodeURIComponent(userId)}`).then(() => undefined),
};
